import React, { useMemo, useState } from 'react';
import { ordersApi } from '@/services/api';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { pageStyles } from '@/components/utils/PageLayout';
import { FilterOption, loadClientOptions } from '@/components/utils/filters';
import { Button, FilterAsyncMultiSelect, loadingStats } from '@/components/ui';
import type { StatItem } from '@/components/ui';
import { useClientApi } from '@/hooks/useClientApi';
import { formatClientName, formatCurrency, formatDate, formatNumber, formatPercentage } from '@/utils/formatters';
import { sumBy } from '@/utils/aggregate';

interface ClientCashData {
  client_id: number;
  client_code: string;
  client_name: string;
  instant_cash: string;
  aum: string;
  cash_plus_liquid: string;
  utilized_cash: string;
  cash_plus_liquid_pct: string;
  t_1_receivables: string;
}

/** Money columns, in table order. Drives the totals row. */
const TOTAL_KEYS = [
  'aum', 'instant_cash', 'cash_plus_liquid', 'utilized_cash', 't_1_receivables',
] as const;
type TotalKey = typeof TOTAL_KEYS[number];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const ClientCash: React.FC = () => {
  const { getClients } = useClientApi();
  const [selectedClients, setSelectedClients] = useState<FilterOption[]>([]);
  // True only while the selection is exactly the "Select All" set; any manual edit clears it.
  const [allSelected, setAllSelected] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const [cashData, setCashData] = useState<ClientCashData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [holdingDate, setHoldingDate] = useState<string>('');
  const [sortField, setSortField] = useState<keyof ClientCashData>('client_code');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const handleClientsChange = (options: FilterOption[]) => {
    setSelectedClients(options);
    setAllSelected(false);
  };

  const handleSelectAll = async () => {
    if (allSelected) {
      setSelectedClients([]);
      setAllSelected(false);
      return;
    }
    setSelectingAll(true);
    setError(null);
    try {
      const response = await getClients({ page_size: 1000 });
      setSelectedClients(response.results.map(c => ({
        value: c.id.toString(),
        label: `${c.client_code} - ${formatClientName(c)}`,
      })));
      setAllSelected(true);
    } catch {
      setError('Failed to load clients');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleLoadCashData = async () => {
    if (selectedClients.length === 0) {
      setError('Select at least one client.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await ordersApi.getClientCash({
        client_ids: selectedClients.map(o => Number(o.value)),
      });
      setCashData(response.cash_data);
      setHoldingDate(response.holding_date);
      setCurrentPage(1);
    } catch (err) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr?.response?.data?.error || apiErr?.message || 'Failed to load cash data');
      setCashData([]);
    } finally {
      setHasFetched(true);
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    const f = field as keyof ClientCashData;
    if (sortField === f) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(f);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const filteredAndSorted = useMemo(() => {
    const query = searchTerm.toLowerCase();
    const matching = cashData.filter(row =>
      row.client_code.toLowerCase().includes(query) ||
      row.client_name.toLowerCase().includes(query)
    );

    const isText = sortField === 'client_code' || sortField === 'client_name';
    return [...matching].sort((a, b) => {
      const valA = isText ? String(a[sortField] ?? '').toLowerCase() : Number(a[sortField]) || 0;
      const valB = isText ? String(b[sortField] ?? '').toLowerCase() : Number(b[sortField]) || 0;
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [cashData, searchTerm, sortField, sortDir]);

  const totalCount = filteredAndSorted.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedData = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredAndSorted, currentPage, pageSize],
  );

  const baseColumns: ColumnDef<ClientCashData>[] = [
    {
      key: 'client_code',
      header: 'Client Code',
      sortable: true,
      render: row => <span className={pageStyles['cell-strong']}>{row.client_code}</span>,
      renderFooter: () => 'Totals',
    },
    {
      key: 'client_name',
      header: 'Client Name',
      sortable: true,
      render: row => row.client_name,
      renderFooter: rows => (
        <span className={pageStyles['cell-sub']}>
          {rows.length} client{rows.length === 1 ? '' : 's'} (all pages)
        </span>
      ),
    },
    { key: 'aum', header: 'AUM', align: 'right', sortable: true, render: row => formatCurrency(row.aum) },
    { key: 'instant_cash', header: 'Instant Cash', align: 'right', sortable: true, render: row => formatCurrency(row.instant_cash) },
    { key: 'cash_plus_liquid', header: 'Cash + Liquid', align: 'right', sortable: true, render: row => formatCurrency(row.cash_plus_liquid) },
    {
      key: 'cash_plus_liquid_pct',
      header: 'Cash + Liquid (%)',
      align: 'right',
      sortable: true,
      render: row => formatPercentage(row.cash_plus_liquid_pct),
      // A share of each client's own AUM — a column of these does not add up.
      renderFooter: () => null,
    },
    { key: 'utilized_cash', header: 'Utilized Cash', align: 'right', sortable: true, render: row => formatCurrency(row.utilized_cash) },
    { key: 't_1_receivables', header: 'T-1 Receivables', align: 'right', sortable: true, render: row => formatCurrency(row.t_1_receivables) },
  ];

  // Totals are attached by column key, so a figure can never land under the wrong header.
  const columns: ColumnDef<ClientCashData>[] = baseColumns.map(col =>
    TOTAL_KEYS.includes(col.key as TotalKey)
      ? { ...col, renderFooter: rows => formatCurrency(sumBy(rows, row => Number(row[col.key as TotalKey]))) }
      : col
  );

  const stats: StatItem[] | undefined = loading
    ? loadingStats(['Holding date', 'Clients loaded'])
    : cashData.length > 0 ? [
    { label: 'Holding date', value: holdingDate ? formatDate(holdingDate) : '—' },
    { label: 'Clients loaded', value: formatNumber(cashData.length, 0) },
  ] : undefined;

  // The client picker stays visible in the bar rather than behind the Filters
  // popover: nothing renders until clients are chosen, so it is the page's
  // primary control, not a way to narrow results already on screen.
  const clientPicker = (
    <>
      <div className={pageStyles['bar-control']}>
        <FilterAsyncMultiSelect
          value={selectedClients}
          onChange={handleClientsChange}
          label="Clients"
          loadOptions={loadClientOptions}
          placeholder="Select clients by code or name…"
        />
      </div>
      <Button onClick={handleSelectAll} disabled={selectingAll || loading}>
        {selectingAll ? 'Selecting…' : allSelected ? 'Deselect All' : 'Select All'}
      </Button>
      <Button variant="primary" onClick={handleLoadCashData} disabled={loading || selectedClients.length === 0}>
        {loading ? 'Loading…' : 'Load Cash Data'}
      </Button>
    </>
  );

  return (
    <DataTable
      title="Client Cash"
      subtitle="Cash, liquid and receivables per client, as at the last processed holding date."
      stats={stats}
      data={paginatedData}
      footerRows={filteredAndSorted}
      columns={columns}
      rowKey={row => row.client_id}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading cash data…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchTerm}
      onSearchChange={value => { setSearchTerm(value); setCurrentPage(1); }}
      searchPlaceholder="Search loaded clients…"
      searchRowSlot={clientPicker}
      emptyMessage={hasFetched
        ? 'No cash data found for the selected clients.'
        : 'Select one or more clients and click Load Cash Data.'}
      fitContent
    />
  );
};

export default ClientCash;
